/*
* Copyright (C) 2025 JPMorgan Chase & Company
* All rights reserved.
*
* Redistribution and use in source and binary forms, with or without
* modification, is not permitted.
*/

import PasswordInput from "../PasswordInput/PasswordInput";
import {useContext, useEffect, useState} from "react";
import {ERROR_ALL_FIELDS_REQUIRED} from "../../constants/constants";
import './PasswordInputGroup.scss';
import PasswordRequirements from "./PasswordRequirements/PasswordRequirements";
import {apiGet} from "../../service/service";
import {TuxLoadingIndicator} from "@tuxedo/react-component-library";
import AppContext from "../../AppContext";
import parse from "html-react-parser";
import {sanitizedHtml} from "../../utils/utils";
import {ExdsValidationGroup, ExdsValidationItem} from "@exds/controls";

export default function PasswordInputGroup(props) {
  const {
    formSubmitted,
    passwordTypeLabel,
    firstInputLabel,
    type,
    setIsButtonDisabled,
    showRequirements,
    showStepper,
    showCharCount,
    afterSubmitChecks,
    setPassphraseTooltip,
    setError
  } = props;
  const {sidContext} = useContext(AppContext);
  const [passwordValue, setPasswordValue] = useState('');
  const [confirmPasswordValue, setConfirmPasswordValue] = useState('');
  const [showPasswordsDontMatchError, setShowPasswordsDontMatchError] = useState(false);
  const [requirements, setRequirements] = useState(null);
  const [activeRequirementsBox, setActiveRequirementsBox] = useState(false);
  const [allRequirementsMet, setAllRequirementsMet] = useState(false);
  const [requirementsMet, setRequirementsMet] = useState(0);
  const [totalRequirements, setTotalRequirements] = useState(9);
  const [isLoading, setIsLoading] = useState(false);
  const [ariaLabel, setAriaLabel] = useState('');

  const sid = sidContext.at(0);

  const passwordInputInvalid = formSubmitted && passwordValue.length === 0;
  const confirmPasswordInputInvalid = formSubmitted && confirmPasswordValue.length === 0;
  const passwordsDoNotMatch = (formSubmitted && confirmPasswordValue.length !== 0 && passwordValue !== confirmPasswordValue) || showPasswordsDontMatchError;
  const PASSWORDS_DONT_MATCH = `${passwordTypeLabel}s don't match`;


  useEffect(() => {
    const errorMessages = document.querySelectorAll('[aria-describedby*="error-text"]');
    const validationErrorMessage = document.querySelectorAll('[aria-describedby*="label-validation"]');

    if (formSubmitted) {
      if (errorMessages.length > 0) {
        errorMessages[0].focus();
      } else if (validationErrorMessage.length > 0) {
        validationErrorMessage[0].focus();
      }
    }
  }, [formSubmitted]);

  useEffect(() => {
    const totalReqsMet = document.querySelectorAll('[aria-label="Requirement met"]').length;
    setRequirementsMet(totalReqsMet);
    setAllRequirementsMet(totalReqsMet === totalRequirements);
  }, [passwordValue]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setAriaLabel(`${requirementsMet} of ${totalRequirements} requirements met`);
  }, [requirementsMet, totalRequirements]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (setIsButtonDisabled) {
      setIsButtonDisabled(!(allRequirementsMet && confirmPasswordValue && !showPasswordsDontMatchError));
    }
  }, [allRequirementsMet, confirmPasswordValue, showPasswordsDontMatchError]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const getPasswordPolicy = async () => {
      setIsLoading(true);
      const idPolicyUrl = `/api/idpolicy?credentialType=${type}&sid=${sid}`;
      apiGet(idPolicyUrl)
        .then((result) => {
          if (result && result.validationRules && result.validationRules.length > 0) {
            setRequirements(result);
            setTotalRequirements(result.validationRules.length);
            setPassphraseTooltip && setPassphraseTooltip(parse(sanitizedHtml(result.tooltip)));
          } else {
            setError({hasCredentialPolicyError: true, correlationId: result.loggingId});
          }

        }).catch((error) =>
          setError({hasCredentialPolicyError: true, correlationId: error.loggingId})
        ).finally(() => setIsLoading(false));
    };

    showRequirements && getPasswordPolicy();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="group-container">
      { isLoading && <div className="d-flex justify-content-center"><TuxLoadingIndicator/></div> }
      { !isLoading && (
        <>
          <PasswordInput
            id='password'
            label={`${firstInputLabel !== undefined ? firstInputLabel : 'New'} ${passwordTypeLabel}`}
            value={passwordValue}
            onClick={() => setActiveRequirementsBox(true)}
            onChange={(e) => setPasswordValue(e.target.value)}
            errorText={passwordInputInvalid ? ERROR_ALL_FIELDS_REQUIRED : ''}
            onBlur={() => setActiveRequirementsBox(false)}
            allRequirementsMet={allRequirementsMet}
            showStepperData={showStepper}
            showCharCount={showCharCount}
            required
          />

          { showRequirements && requirements?.validationRules?.length > 0 &&
            <>
              <PasswordRequirements
                requirements={requirements}
                afterSubmitChecks={afterSubmitChecks}
                activeRequirementsBox={activeRequirementsBox}
                passwordValue={passwordValue}
                requirementsMet={requirementsMet}
              />
              <div aria-live="assertive" className='requirements-met-message'>{ariaLabel}</div>
            </>
          }

          <PasswordInput
            id='confirm-password'
            label={`Confirm ${passwordTypeLabel}`}
            value={confirmPasswordValue}
            onChange={(e) => {
              const value = e.target.value;
              setConfirmPasswordValue(e.target.value);
              setShowPasswordsDontMatchError(value.length !== 0 && passwordValue !== value);
            }}
            errorText={confirmPasswordInputInvalid ? ERROR_ALL_FIELDS_REQUIRED : ''}
            validationGroup={
              passwordsDoNotMatch &&
              <ExdsValidationGroup data-testid="input-field-validation">
                <ExdsValidationItem status="error" text={PASSWORDS_DONT_MATCH}/>
              </ExdsValidationGroup>
            }
            onBlur={(e) => {
              e.stopPropagation();
              setShowPasswordsDontMatchError(confirmPasswordValue.length !== 0 && passwordValue !== confirmPasswordValue);
            }}
            required
          />
        </>
      )}
    </div>
  );
}
